import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Team, TeamService } from '../../services/team.service';
import { UsersIcon, TrashIcon, PencilIcon, ArrowPathIcon, ArrowRightIcon, ShieldExclamationIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '../../hooks/useRedux';
import toast from 'react-hot-toast';
import DeleteTeamModal from './DeleteTeamModal';
import CreateTeamModal from './CreateTeamModal';
import UpgradePromptModal from '../common/UpgradePromptModal';
import { hasPermission } from '../../utils/permissionUtils';
// import EditTeamModal from './EditTeamModal';

interface TeamCardProps {
  team: Team;
  onDeleted: () => void;
  onUpdated: () => void;
  onRefresh: () => void;
  storageData?: {
    used: number;
    total: number;
    percentage: number;
    items: number;
  };
  isRefreshing?: boolean;
  onViewDepartments?: () => void;
  isDepartment?: boolean;
  isOrganization?: boolean;
}

const TeamCard: React.FC<TeamCardProps> = ({
  team,
  onDeleted,
  onUpdated,
  onRefresh,
  storageData,
  isRefreshing = false,
  onViewDepartments,
  isDepartment = false,
  isOrganization = false
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { permissions, role_id } = useAppSelector(state => state.auth);
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/team/${team.id}`);
  };

  const formatStorage = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateUsagePercentage = (): number => {
    if (!storageData || storageData.total === 0) return 0;
    return (storageData.used / storageData.total) * 100;
  };

  const usagePercentage = calculateUsagePercentage();
  const usageColor = usagePercentage < 70
    ? 'bg-green-500'
    : (usagePercentage < 90 ? 'bg-yellow-500' : 'bg-red-500');

  const handleRefreshStorage = async (teamId: string) => {
    try {
      await TeamService.fetchTeamStorageUsage(teamId);
      toast.success('Team storage refreshed successfully');
      onUpdated();
    } catch (error) {
      console.error('Error refreshing team storage:', error);
      toast.error('Failed to refresh team storage');
    }
  };

  // Calculate days remaining in trial
  const getDaysRemaining = () => {
    if (!team.created_at) return 0;
    const trialEnd = new Date(new Date(team.created_at).getTime() + 14 * 24 * 60 * 60 * 1000);
    const today = new Date();
    const diffTime = trialEnd.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining();
  const memberUsagePercentage = ((team?.current_member_count || 0) / (team?.member_limit || 1)) * 100;


  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:scale-[1.02]">
      {/* Gradient Header */}
      <div className="relative bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 px-5 py-4">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-20 h-10 bg-white rounded-full -translate-y-5 translate-x-10"></div>
          <div className="absolute bottom-0 left-0 w-12 h-6 bg-white rounded-full translate-y-3 -translate-x-6"></div>
        </div>
        
        <div className="relative">
          <Link to={`/team/${team.id}`} className="block">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 flex-shrink-0">
                  <UserGroupIcon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-white truncate">
                    {team.name}
                  </h3>
                  <p className="text-white/80 text-sm truncate">
                    {team.description || 'No description'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center text-white/90">
              <UsersIcon className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="text-sm font-medium">
                {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
              </span>
            </div>
          </Link>
        </div>
      </div>

      {/* Content Section */}
      <div className="">

        {/* Member Usage Section */}
        {role_id == 1 && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Members ({team?.current_member_count ?? 0}/{team?.member_limit ?? 0})
              </span>
              {(team?.current_member_count ?? 0) >= (team?.member_limit ?? 0) && (
                <span className="text-xs text-red-600 dark:text-red-400 flex items-center bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                  <ShieldExclamationIcon className="h-3 w-3 mr-1" />
                  Full
                </span>
              )}
              {(team?.current_member_count ?? 0) < (team?.member_limit ?? 0) &&
                (team?.current_member_count ?? 0) >= ((team?.member_limit ?? 0) * 0.9) && (
                  <span className="text-xs text-orange-600 dark:text-orange-400 flex items-center bg-orange-50 dark:bg-orange-900/20 px-1.5 py-0.5 rounded-full">
                    <ShieldExclamationIcon className="h-3 w-3 mr-1" />
                    Near
                  </span>
                )}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${(team?.current_member_count ?? 0) >= (team?.member_limit ?? 0)
                    ? 'bg-red-500'
                    : (team?.current_member_count ?? 0) >= ((team?.member_limit ?? 0) * 0.9)
                      ? 'bg-orange-500'
                      : 'bg-green-500'
                  }`}
                style={{ width: `${Math.min(((team?.current_member_count ?? 0) / (team?.member_limit ?? 1)) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}
        
        {storageData && role_id === 1 && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Storage ({formatStorage(storageData.used)}/{formatStorage(storageData.total)})
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full">
                {storageData.items}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${usageColor}`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
            {usagePercentage >= 90 && (
              <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-1.5 rounded text-center">
                Storage nearly full!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action Footer */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-t border-gray-200 dark:border-gray-600 px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          {role_id === 1 ? (
            <div className="flex flex-wrap gap-1.5">
              <Link
                to={`/team/${team.id}`}
                className="inline-flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Details
              </Link>
              <Link
                to={`/team/${team.id}/transactions`}
                className="inline-flex items-center px-3 py-1.5 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg transition-all duration-200"
              >
                Transactions
              </Link>
            </div>
          ) : (
            <Link
              to={`/team/${team.id}`}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              View details
            </Link>
          )}

          <div className="flex items-center gap-1">
            {isOrganization && role_id === 1 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRefresh();
                }}
                className={`p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isRefreshing}
                title="Refresh storage usage"
              >
                <ArrowPathIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin text-primary-500' : ''}`} />
              </button>
            )}
            {hasPermission(permissions, 'team', 'update_teams') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
                title="Edit Team"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            {hasPermission(permissions, 'team', 'delete_teams') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
                className="p-2 rounded-lg text-red-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                title="Delete Team"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* {onViewDepartments && (
        <button
          onClick={onViewDepartments}
          className="mt-2 w-full py-2 px-4 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 flex items-center justify-center"
        >
          <span>View Departments</span>
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </button>
      )} */}

      <DeleteTeamModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        teamId={team.id}
        teamName={team.name}
        onDeleted={onDeleted}
        isDepartment={isDepartment}
        role_id={role_id ?? 1}
      />
      <CreateTeamModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onTeamCreated={onUpdated}
        editMode={true}
        teamData={team}
        isDepartment={isDepartment}
      />
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          navigate(`/upgrade?teamId=${team.id}`);
        }}
        teamId={team.id}

      />
    </div>
  );
};

export default TeamCard;